import React, { useState, forwardRef, useImperativeHandle, Ref } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import EntityReferenceComponent from "./EntityReferenceComponent";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { StyledBox, StyledFormControl } from "./EntitySelectionInput.styles";

export interface NodeEntity {
  published_id: string;
  _key: string;
}

export interface EntitySelectionProps<T extends NodeEntity> {
  open: boolean;
  required?: boolean;
  initialSelectedEntity?: T | null;
  onClose: () => void;
  handleEntitySelect: (selectedEntity: T | null) => void;
}

export interface EntitySelectionInputProps<T extends NodeEntity> {
  value?: T | null;
  entityCollection: string;
  id?: string;
  name?: string;
  buttonLabel?: string;
  onChange: (e: { target: { value: T | null; name?: string } }) => void;
  onBlur?: () => void;
  disabled?: boolean;
  static?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  renderEntitySelection?: (props: EntitySelectionProps<T>) => React.ReactNode;
  fullWidth?: boolean;
  size?: "small" | "medium";
}

function EntitySelectionInputInner<T extends NodeEntity>(
  {
    value = null,
    entityCollection,
    id,
    name,
    buttonLabel,
    onChange,
    onBlur,
    disabled = false,
    required = false,
    placeholder,
    error = false,
    helperText,
    renderEntitySelection,
    fullWidth = false,
    size = "medium",
  }: EntitySelectionInputProps<T>,
  ref: Ref<any>
) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClick = () => setIsModalOpen(true);

  const handleSelect = (selected: T | null) => {
    onChange({ target: { value: selected, name } });
    setIsModalOpen(false);
  };

  const handleClear = () => {
    onChange({ target: { value: null, name } });
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      const el = document.getElementById(id ?? "");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  }));

  return (
    <StyledFormControl
      id={id}
      error={error}
      fullWidth={fullWidth}
      size={size}
      required={required}
      disabled={disabled}
      onBlur={onBlur}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <StyledBox error={error} className={clsx(disabled && "Mui-disabled")}>
          <Box flexGrow={1} p={1.5}>
            {value && value._key ? (
              <EntityReferenceComponent
                entity={value}
                entityCollection={entityCollection}
                showCatalogNo={false}
              />
            ) : (
              <Typography color="text.secondary" variant="body2">
                {placeholder || "No selection made"}
              </Typography>
            )}
          </Box>
          {!required && value && (
            <IconButton
              size="small"
              onClick={handleClear}
              aria-label="clear selection"
              disabled={disabled}
              sx={{ mr: 1 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </StyledBox>
        <Button
          variant="contained"
          size="medium"
          onClick={handleClick}
          disabled={disabled}
        >
          {buttonLabel ?? "Select"}
        </Button>
      </Box>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {renderEntitySelection &&
        renderEntitySelection({
          open: isModalOpen,
          onClose: () => setIsModalOpen(false),
          handleEntitySelect: handleSelect,
          initialSelectedEntity: value,
          required,
        })}
    </StyledFormControl>
  );
}

const EntitySelectionInput = forwardRef(EntitySelectionInputInner) as <
  T extends NodeEntity,
>(
  props: EntitySelectionInputProps<T> & { ref?: Ref<any> }
) => ReturnType<typeof EntitySelectionInputInner>;

export default EntitySelectionInput;
